import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  Input,
  OnInit,
  signal
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { codeToHtml } from 'shiki';

import { StepperFormStore } from '../../../+state/firmware-package/stepper-form.store';
import { extractVersionFromXmlContent } from '../../../helpers/extractVersionFromXmlContent';
import { formateManifestFromBackend } from '../../../helpers/formatManifestFromBackend';
import { readXmlFromZip } from '../../../helpers/readXmlFromZip';

@Component({
  standalone: true,
  selector: 'remote-update-domain-manifest-information',
  imports: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './manifest-information.html'
})
export class ManifestInformation implements OnInit {
  private stepperFormStore = inject(StepperFormStore);
  private sanitizer = inject(DomSanitizer);
  @Input() currentSection = '';

  extractedXmlContent = signal<SafeHtml | null>(null);
  private initializedFromBackend = false;

  ngOnInit(): void {
    const manifestData = this.stepperFormStore.manifestInfo().manifestName;
    if (this.stepperFormStore.isEditingFirmwarePackage() && manifestData) {
      const version = extractVersionFromXmlContent(manifestData);
      this.stepperFormStore.setUploadedManifestVersion(version);
      const prettyManifest = formateManifestFromBackend(manifestData);
      this.renderHighlightedXml(prettyManifest);

      this.stepperFormStore.updateManifestInfo({
        manifestName: manifestData,
        isValid: true
      });

      this.initializedFromBackend = true;
    }
  }

  constructor() {
    effect(() => {
      const uploadedFiles = this.stepperFormStore.selectedFiles();

      if (this.initializedFromBackend) return;

      if (uploadedFiles.length === 0) {
        this.extractedXmlContent.set(null);
        this.stepperFormStore.updateManifestInfo({ isValid: false });
        return;
      }

      this.loadAndRenderManifest(uploadedFiles[0]);
    });
  }

  private async loadAndRenderManifest(selectedFile: File) {
    try {
      const xmlContent = await readXmlFromZip(selectedFile);
      await this.renderHighlightedXml(xmlContent);
      this.stepperFormStore.updateManifestInfo({
        manifestName: selectedFile.name.split('.')[0],
        isValid: true
      });
    } catch (err) {
      const errorMessage = (err as Error).message;
      this.stepperFormStore.updateManifestInfo({ isValid: false }, errorMessage);
    }
  }

  private async renderHighlightedXml(xmlContent: string) {
    const highlightedXml = await codeToHtml(xmlContent, {
      lang: 'xml',
      theme: 'light-plus'
    });

    this.extractedXmlContent.set(this.sanitizer.bypassSecurityTrustHtml(highlightedXml));
  }
}
